import React, { useState, useRef, useEffect } from 'react';
import styles from './VideoTimeline.module.css';
import { Caption } from '../../types';

interface SegmentMarkersProps {
  segments: Caption[];
  duration: number;
  segmentColors: string[];
  onSegmentClick: (e: React.MouseEvent, segment: Caption) => void;
  // Added setSegments to update segments on resize.
  setSegments: React.Dispatch<React.SetStateAction<Caption[]>>;
}

type ResizeType = 'left' | 'right';

interface ResizingState {
  segmentId: string;
  type: ResizeType;
  initialX: number;
  initialStart: number;
  initialEnd: number;
}

const SegmentMarkers: React.FC<SegmentMarkersProps> = ({
  segments,
  duration,
  segmentColors,
  onSegmentClick,
  setSegments,
}) => {
  // State to track the current segment being resized.
  const [resizingState, setResizingState] = useState<ResizingState | null>(null);
  // Ref for the container element (for width measurement).
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper: convert time (in seconds) to a percentage of the total duration.
  const timeToPercent = (time: number) => (time / duration) * 100;

  // When a resize handle is pressed, store the initial data.
  const handleResizeStart = (
    e: React.MouseEvent,
    segment: Caption,
    type: ResizeType
  ) => {
    e.stopPropagation();
    if (containerRef.current) {
      // Optionally, you can store the container’s dimensions if needed.
    }
    setResizingState({
      segmentId: segment.id,
      type,
      initialX: e.clientX,
      initialStart: segment.start,
      initialEnd: segment.end,
    });
  };

  // Global listeners for resizing:
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (resizingState && containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Calculate how many seconds each pixel represents.
        const timePerPixel = duration / containerWidth;
        const deltaX = e.clientX - resizingState.initialX;
        const deltaTime = deltaX * timePerPixel;
        setSegments((prevSegments) =>
          prevSegments.map((seg) => {
            if (seg.id === resizingState.segmentId) {
              if (resizingState.type === 'left') {
                // New start should not be after (end - 0.1) and not less than 0.
                const newStart = Math.min(
                  seg.end - 0.1,
                  Math.max(0, resizingState.initialStart + deltaTime)
                );
                return { ...seg, start: newStart };
              } else {
                // New end should not be before (start + 0.1).
                const newEnd = Math.max(
                  seg.start + 0.1,
                  resizingState.initialEnd + deltaTime
                );
                return { ...seg, end: newEnd };
              }
            }
            return seg;
          })
        );
      }
    };

    const handleGlobalMouseUp = () => {
      if (resizingState) {
        setResizingState(null);
      }
    };

    if (resizingState) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [resizingState, duration, setSegments]);

  return (
    <div ref={containerRef}>
      {segments.map((segment, index) => {
        const leftPercent = timeToPercent(segment.start);
        const widthPercent = timeToPercent(segment.end - segment.start);
        return (
          <div
            key={segment.id || index}
            className={styles.segmentMarker}
            style={{
              left: `${leftPercent}%`,
              width: `${widthPercent}%`,
              backgroundColor: segmentColors[index % segmentColors.length],
            }}
            onClick={(e) => onSegmentClick(e, segment)}
          >
            <div className={styles.segmentText}>{segment.text}</div>
            {/* Left resize handle */}
            <div
              className={styles.resizeHandleLeft}
              onMouseDown={(e) => handleResizeStart(e, segment, 'left')}
              title="Drag to adjust start time"
            />
            {/* Right resize handle */}
            <div
              className={styles.resizeHandleRight}
              onMouseDown={(e) => handleResizeStart(e, segment, 'right')}
              title="Drag to adjust end time"
            />
          </div>
        );
      })}
    </div>
  );
};

export default SegmentMarkers;
